import { State, useEpiserver } from '@episerver/spa-core';
import { connect } from 'react-redux';
import HoyesterettLayout, { CurrentContent } from '../Shared/Old/Layout/HoyesterettLayout';
import { RootLayoutProps } from './RootLayoutProps';
import useLayoutSettings, { LayoutSettingsContext } from './Hooks/useLayoutSettings';
import { LocalizationProvider } from './Hooks/useLocalizationContext';
import { isHoyesterettPage, shouldRenderEmptyLayout } from './utils';
import { useCurrentContent } from './Hooks/useCurrentContent';
import EmptyLayout from './SiteLayouts/EmptyLayout';
import SSRLayout from './SiteLayouts/SSRLayout';
import DefaultLayout from './SiteLayouts/DefaultLayout';
import { DdsProvider } from '@norges-domstoler/dds-components';

export type DdsLanguage = 'no' | 'en' | 'se';

export type DdsRootLayoutProps = Omit<RootLayoutProps, 'currentLanguage'> & {
    currentLanguage: DdsLanguage;
};

const RootLayout = ({ ...props }: DdsRootLayoutProps) => {
    const ctx = useEpiserver();
    const { currentLanguage, children } = props;

    const { currentContent, pathName } = useCurrentContent();
    const { layoutSettings } = useLayoutSettings(currentLanguage);

    if (ctx.isServerSideRendering()) {
        return <SSRLayout lang={currentLanguage} children={children} />;
    }

    if (shouldRenderEmptyLayout(currentContent, ctx)) {
        return <EmptyLayout />;
    }

    if (isHoyesterettPage(currentContent)) {
        const loadedCurrentContent = currentContent as CurrentContent;

        return (
            <LocalizationProvider>
                <HoyesterettLayout
                    {...props}
                    currentContent={loadedCurrentContent}
                    layoutSettings={layoutSettings?.feedbackDisclaimer}
                    pathname={pathName}
                />
            </LocalizationProvider>
        );
    }

    return (
        <LocalizationProvider>
            <LayoutSettingsContext.Provider value={layoutSettings}>
                <DefaultLayout {...props} pathname={pathName} context={ctx} />
            </LayoutSettingsContext.Provider>
        </LocalizationProvider>
    );
};

function toDdsLanguage(lang: string | undefined): DdsLanguage {
    if (lang === 'en' || lang === 'se') return lang;
    return 'no';
}

const withDdsProvider = (Component: typeof RootLayout) => {
    return ({ currentLanguage, ...rest }: RootLayoutProps) => {
        const ddsLanguage = toDdsLanguage(currentLanguage);
        return (
            <DdsProvider theme="public-light" language={ddsLanguage}>
                <Component {...rest} currentLanguage={ddsLanguage} />
            </DdsProvider>
        );
    };
};

export const ConnectedRootLayout = connect((state: State.CmsAppState, ownProps: RootLayoutProps) => {
    const propsFromState = state.OptiContentCloud || {};
    return { ...ownProps, ...propsFromState };
})(withDdsProvider(RootLayout));

export default ConnectedRootLayout;
